import React from "react";
import {
  Box,
  Chip,
  Link,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { formatDate } from "../common/functions/formatDate";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import { Voucher } from "../../../models/Vouchers/Voucher";

interface VouchersTableProp {
  maxHeight: number;
  vouchers: Voucher[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  sortType: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string, sortType: SortType) => void;
}

const VouchersTable = ({
  maxHeight,
  vouchers,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}: VouchersTableProp) => {
  const theme = useTheme<Theme>();

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      {vouchers.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor:
                        theme.palette.customColors?.lightGreyColor ||
                        theme.palette.grey[100],
                      minWidth: "60px",
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Voucher No" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Customer Name" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Order ID" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    PR ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Brand Code" fontSize="0.8rem" />
                  <AppTableHeaderCell title="Product ID" fontSize="0.8rem" />
                  <AppTableHeaderCell title="GUID" fontSize="0.8rem" />
                  <AppTableHeaderCell title="PIN" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    Value
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    title="Product Denomination"
                    fontSize="0.8rem"
                  />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "end_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "end_date",
                          sortBy === "end_date" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("end_date")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      End Date
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Job ID
                  </AppTableHeaderCell>
                  <AppTableHeaderCell fontSize="0.8rem">
                    Status
                  </AppTableHeaderCell>
                  <AppTableHeaderCell title="Admin Name" fontSize="0.8rem" />
                  <AppTableHeaderCell fontSize="0.8rem">
                    <TableSortLabel
                      active={sortBy === "created_at"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange(
                          "created_at",
                          sortBy === "created_at" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_at")}</span>
                      )}
                      sx={{ fontSize: "0.8rem" }}
                    >
                      Created At
                    </TableSortLabel>
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher) => {
                  return (
                    <TableRow
                      key={voucher.guid}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                    >
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 15,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          color:
                            theme.palette.customColors?.greyTextColor ||
                            theme.palette.text.secondary,
                          minWidth: "60px",
                        }}
                      >
                        {voucher.id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.no || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.customer_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        <Link
                          href={`/orders/add-orders?orderId=${voucher.order_id}`}
                          target="_blank"
                          sx={{
                            textDecoration: "underline",
                            color:
                              theme.palette.customColors?.text ||
                              theme.palette.primary.main,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {voucher.order_id || "-"}
                        </Link>
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        <Link
                          href={`/purchase/requests/details?prId=${voucher.pr_id}&projectId=${voucher.order_id}`}
                          target="_blank"
                          sx={{
                            textDecoration: "underline",
                            color:
                              theme.palette.customColors?.text ||
                              theme.palette.primary.main,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {voucher.pr_id || "-"}
                        </Link>
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.brand_code || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        <Link
                          href={`/products/add-edit-product?product-id=${voucher.product_id}`}
                          target="_blank"
                          sx={{
                            textDecoration: "underline",
                            color:
                              theme.palette.customColors?.text ||
                              theme.palette.primary.main,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {voucher.product_id || "-"}
                        </Link>
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.guid || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.pin || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.value
                          ? `₹${parseFloat(voucher.value).toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}`
                          : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.product_denomination || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.end_date ? formatDate(voucher.end_date) : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.job_id || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.active !== undefined ? (
                          <Chip
                            label={voucher.active ? "Active" : "Cancelled"}
                            size="small"
                            color={voucher.active ? "success" : "error"}
                            variant="outlined"
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.admin_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        {voucher.created_at
                          ? formatDate(voucher.created_at)
                          : "-"}
                      </AppTableRowCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
    </Paper>
  );
};

export default VouchersTable;
